import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight, BookOpen, Briefcase, CheckCircle2, Clock, GraduationCap,
  MessageCircle, Sparkles, Trophy, Users,
} from "lucide-react";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingButtons from "@/components/FloatingButtons";
import ConsultationDialog from "@/components/ConsultationDialog";
import CourseLeadCard from "@/components/CourseLeadCard";
import { useContent } from "@/lib/content";
import { getInitials } from "@/lib/image";
import useSEO from "@/lib/useSEO";
import { SITE, buildWhatsAppLink, getContact } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COURSE_TO_API = { acca: "ACCA", "cma-usa": "CMA USA" };

export default function CoursePage() {
  const { slug } = useParams();
  const { content } = useContent();
  const course = content.courses?.[slug];
  const contact = getContact(content);
  const [open, setOpen] = useState(false);
  const [toppers, setToppers] = useState([]);

  useEffect(() => {
    const apiCourse = COURSE_TO_API[slug];
    if (!apiCourse) { setToppers([]); return; }
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/toppers`);
        if (cancel) return;
        const filtered = (data || []).filter((t) => t.featured && t.course === apiCourse).slice(0, 6);
        setToppers(filtered);
      } catch {
        // fail silently
      }
    })();
    return () => { cancel = true; };
  }, [slug]);

  useSEO({
    title: course?.meta_title,
    description: course?.meta_description,
    canonical: typeof window !== "undefined" ? window.location.href : undefined,
    image: SITE.media.teamWithLogo,
    jsonLd: course ? {
      "@context": "https://schema.org",
      "@type": "Course",
      name: `${course.name} — ${course.full_name}`,
      description: course.meta_description,
      provider: {
        "@type": "EducationalOrganization",
        name: SITE.name,
        sameAs: contact.email,
        address: contact.address,
        telephone: contact.phone,
      },
      hasCourseInstance: {
        "@type": "CourseInstance",
        courseMode: "blended",
        location: {
          "@type": "Place",
          name: SITE.name,
          address: contact.address,
        },
      },
    } : null,
  });

  if (!course) {
    // Wait for content load before redirecting
    if (!content || Object.keys(content).length === 0) return null;
    return <Navigate to="/" replace />;
  }

  const isACCA = slug === "acca";
  const featuredToppers = toppers;

  return (
    <div className="App font-inter text-slate-800">
      <Header onConsult={() => setOpen(true)} />

      {/* Hero */}
      <section
        data-testid={`course-page-hero-${slug}`}
        className="relative bg-[#002570] text-white overflow-hidden"
      >
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top_right,_#73c3ff,_transparent_60%)]" />
        <div className="absolute inset-0 bg-grid-soft opacity-10 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Link to="/" className="inline-flex items-center gap-2 font-inter text-sm text-[#73c3ff] hover:text-white" data-testid="course-back-home-link">
              ← Back to home
            </Link>
            <div className="mt-4 inline-flex items-center gap-2 bg-[#73c3ff]/15 text-[#73c3ff] px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase">
              <Sparkles className="w-3.5 h-3.5" />
              {course.issuer}
            </div>
            <h1 className="mt-5 font-rubik text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-balance">
              {course.name} — <span className="text-[#73c3ff]">{course.tagline}</span>
            </h1>
            <p className="mt-5 font-inter text-base md:text-lg text-white/85 max-w-2xl leading-relaxed">
              Educators Institute of Management. Studied in our U-shape interactive batches with mentor support, retake coaching and 100% guidance till you pass and get placed.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button
                onClick={() => setOpen(true)}
                data-testid={`course-hero-consult-${slug}`}
                className="inline-flex items-center gap-2 bg-[#73c3ff] text-[#002570] hover:bg-white rounded-full px-7 py-3.5 font-inter font-semibold transition-all hover:shadow-2xl"
              >
                Book Free Career Consultation <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href={buildWhatsAppLink(`Hi Educators Institute, I'd like to know more about ${course.name}.`, contact.whatsappRaw)}
                target="_blank"
                rel="noreferrer"
                data-testid={`course-hero-wa-${slug}`}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 text-white hover:bg-white/20 rounded-full px-7 py-3.5 font-inter font-medium transition-all"
              >
                <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
              </a>
            </div>
            <div className="mt-9 grid sm:grid-cols-3 gap-4 max-w-xl">
              {[
                { icon: Clock, label: "Duration", value: course.duration },
                { icon: GraduationCap, label: "Levels", value: `${course.levels.length}` },
                { icon: Trophy, label: "Toppers", value: toppers.length > 0 ? `${toppers.length}+` : "Many" },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-4">
                  <Icon className="w-5 h-5 text-[#73c3ff] mb-2" />
                  <p className="font-inter text-xs uppercase tracking-wider text-white/60">{label}</p>
                  <p className="font-rubik text-base font-semibold mt-0.5">{value}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <CourseLeadCard courseName={course.name} source={`course-page-${slug}`} />
          </div>
        </div>
      </section>

      {/* Why This Course */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <p className="font-inter text-sm font-semibold tracking-[0.18em] uppercase text-[#002570]">Why {course.name}</p>
          <h2 className="mt-3 font-rubik text-3xl md:text-4xl font-bold text-[#0f172a] tracking-tight max-w-3xl text-balance">
            The 3 reasons our students choose {course.name}.
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {course.why.map((w, i) => (
              <div key={i} className="bg-[#f8fafc] rounded-2xl p-7 border border-slate-100">
                <div className="w-11 h-11 rounded-xl bg-[#002570] text-white flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <p className="font-inter text-base text-slate-700 leading-relaxed">{w}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Levels & Subjects */}
      <section className="bg-[#f8fafc] py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <p className="font-inter text-sm font-semibold tracking-[0.18em] uppercase text-[#002570]">Levels & Subjects</p>
          <h2 className="mt-3 font-rubik text-3xl md:text-4xl font-bold text-[#0f172a] tracking-tight max-w-3xl text-balance">
            Your roadmap to becoming a <span className="text-[#002570]">{course.full_name}</span>.
          </h2>
          <div className="mt-10 grid lg:grid-cols-3 gap-5">
            {course.levels.map((lvl, i) => (
              <div key={lvl.name} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-9 h-9 rounded-full bg-[#002570] text-white font-rubik font-bold flex items-center justify-center text-sm">{i + 1}</span>
                  <h3 className="font-rubik text-lg font-semibold text-[#0f172a]">{lvl.name}</h3>
                </div>
                <ul className="space-y-2">
                  {lvl.papers.map((p, j) => (
                    <li key={j} className="flex items-start gap-2 font-inter text-sm text-slate-700">
                      <BookOpen className="w-4 h-4 text-[#73c3ff] mt-0.5 shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="font-inter text-sm font-semibold tracking-[0.18em] uppercase text-[#002570]">Who can apply</p>
            <h2 className="mt-3 font-rubik text-3xl md:text-4xl font-bold text-[#0f172a] tracking-tight text-balance">
              Eligibility & best-fit profile.
            </h2>
            <p className="mt-4 font-inter text-slate-600">Not sure if {course.name} fits you? Talk to our counsellor — we'll be honest.</p>
            <button onClick={() => setOpen(true)} data-testid={`course-eligibility-cta-${slug}`} className="mt-6 inline-flex items-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] rounded-full px-6 py-3 font-inter font-medium transition-colors">
              Check your eligibility <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="lg:col-span-7 space-y-3">
            {course.eligibility.map((e, i) => (
              <div key={i} className="flex items-start gap-4 p-5 bg-[#f8fafc] rounded-2xl border border-slate-100">
                <div className="w-10 h-10 rounded-full bg-[#73c3ff]/20 text-[#002570] flex items-center justify-center shrink-0">
                  <Users className="w-5 h-5" />
                </div>
                <p className="font-inter text-base text-slate-800 leading-relaxed">{e}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Paths */}
      <section className="bg-[#002570] text-white py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_bottom_left,_#73c3ff,_transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8">
          <p className="font-inter text-sm font-semibold tracking-[0.18em] uppercase text-[#73c3ff]">Career Paths</p>
          <h2 className="mt-3 font-rubik text-3xl md:text-4xl font-bold tracking-tight max-w-3xl text-balance">
            Where {course.name} can take your career.
          </h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {course.careers.map((c) => (
              <div key={c.title} className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/[0.08] transition-all">
                <div className="w-11 h-11 rounded-xl bg-[#73c3ff] text-[#002570] flex items-center justify-center mb-4">
                  <Briefcase className="w-5 h-5" />
                </div>
                <h3 className="font-rubik text-base font-semibold">{c.title}</h3>
                <p className="mt-2 font-inter text-sm text-[#73c3ff] font-semibold">{c.salary}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured toppers (only for ACCA & CMA which have data) */}
      {featuredToppers.length > 0 && (
        <section className="bg-[#f8fafc] py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
              <div>
                <p className="font-inter text-sm font-semibold tracking-[0.18em] uppercase text-[#002570]">Real Students. Real Results.</p>
                <h2 className="mt-3 font-rubik text-3xl md:text-4xl font-bold text-[#0f172a] tracking-tight text-balance">
                  Recent {course.name} <span className="text-[#002570]">toppers</span> from Educators.
                </h2>
              </div>
              <Link to="/toppers" className="inline-flex items-center gap-2 text-[#002570] font-inter font-medium hover:underline" data-testid={`course-view-all-toppers-${slug}`}>
                View all achievers <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredToppers.map((t, i) => (
                <div key={t.id || i} className="bg-white rounded-2xl p-5 border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl overflow-hidden bg-gradient-to-br from-[#002570] to-[#001a52] text-white flex items-center justify-center font-rubik text-lg font-bold shrink-0">
                    {t.photo ? <img src={t.photo} alt={t.name} className="w-full h-full object-cover" /> : <span>{getInitials(t.name)}</span>}
                  </div>
                  <div className="flex-1">
                    <p className="font-rubik text-base font-bold text-[#0f172a]">{t.name}</p>
                    {isACCA ? (
                      <p className="font-inter text-xs text-slate-600 mt-0.5">
                        {(t.scores || []).map((s) => `${s.paper}: ${s.mark}`).join(" · ")}
                      </p>
                    ) : (
                      <p className="font-inter text-xs text-slate-600 mt-0.5">{t.part} · <span className="font-semibold text-[#002570]">{t.mark}/{t.max_mark}</span></p>
                    )}
                  </div>
                  <Trophy className="w-5 h-5 text-[#73c3ff]" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <p className="font-inter text-sm font-semibold tracking-[0.18em] uppercase text-[#002570] text-center">FAQ</p>
          <h2 className="mt-3 font-rubik text-3xl md:text-4xl font-bold text-[#0f172a] tracking-tight text-center text-balance">
            Common questions about {course.name}.
          </h2>
          <Accordion type="single" collapsible className="mt-10" data-testid={`course-faq-${slug}`}>
            {course.faqs.map((f, i) => (
              <AccordionItem key={i} value={`q-${i}`} className="border-b border-slate-200">
                <AccordionTrigger className="font-rubik text-left text-base md:text-lg font-semibold text-[#0f172a] hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="font-inter text-base text-slate-600 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-12 bg-[#002570] rounded-3xl p-8 md:p-10 text-white text-center">
            <h3 className="font-rubik text-2xl md:text-3xl font-bold tracking-tight">
              Still have questions about {course.name}?
            </h3>
            <p className="mt-3 font-inter text-white/85">A free 15-minute call clears it all up.</p>
            <button
              onClick={() => setOpen(true)}
              data-testid={`course-faq-cta-${slug}`}
              className="mt-6 inline-flex items-center gap-2 bg-white text-[#002570] hover:bg-[#73c3ff] rounded-full px-7 py-3.5 font-inter font-semibold transition-all"
            >
              Book Free Consultation <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
      <ConsultationDialog open={open} onOpenChange={setOpen} source={`course-page-${slug}`} />
    </div>
  );
}
